import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Plus, Edit, Trash2, Globe, MessageSquare, ExternalLink, Image as ImageIcon } from 'lucide-react';
import FileUploadInput from '../../components/admin/FileUploadInput';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PLATFORMS = ['facebook', 'twitter', 'instagram', 'youtube'];

export default function SocialPage() {
  const [profiles, setProfiles] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Post Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postForm, setPostForm] = useState({
    platform: 'facebook',
    content: '',
    post_date: new Date().toISOString().split('T')[0],
    post_url: '',
    image_url: ''
  });
  const [fileData, setFileData] = useState({});

  // Profile Dialog State
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({
    platform: 'facebook',
    url: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [profilesRes, postsRes] = await Promise.all([
        axios.get(`${API_URL}/api/social_feeds`),
        axios.get(`${API_URL}/api/social-posts`)
      ]);
      setProfiles(profilesRes.data);
      setPosts(postsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load social media data');
    } finally {
      setLoading(false);
    }
  };

  // --- Post Handlers ---

  const handleSavePost = async () => {
    try {
      if (!postForm.content.trim()) {
        toast.error('Post content is required');
        return;
      }

      let updatedForm = { ...postForm };

      // Handle file upload
      if (fileData.post_image) {
        const formData = new FormData();
        formData.append('file', fileData.post_image);
        // Move subfolder to query parameter for endpoint standard
        const uploadRes = await axios.post(
          `${API_URL}/api/files/upload?subfolder=social/posts`,
          formData,
          { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
        );
        updatedForm.image_url = `/api/files/${uploadRes.data.id}`;
      }

      if (editingPost) {
        await axios.put(
          `${API_URL}/api/social-posts/${editingPost.id}`,
          updatedForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Post updated successfully!');
      } else {
        await axios.post(
          `${API_URL}/api/social-posts`,
          updatedForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Post created successfully!');
      }

      setDialogOpen(false);
      setEditingPost(null);
      setFileData({});
      resetPostForm();
      await fetchData();
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('Failed to save post');
    }
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`${API_URL}/api/social-posts/${id}`, { headers: getAuthHeaders() });
      toast.success('Post deleted successfully!');
      await fetchData();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setPostForm({
      platform: post.platform,
      content: post.content,
      post_date: post.post_date,
      post_url: post.post_url || '',
      image_url: post.image_url || ''
    });
    setDialogOpen(true);
  };

  const resetPostForm = () => {
    setPostForm({
      platform: 'facebook',
      content: '',
      post_date: new Date().toISOString().split('T')[0],
      post_url: '',
      image_url: ''
    });
  };

  // --- Profile Handlers ---

  const handleSaveProfile = async () => {
    try {
      if (!profileForm.url.trim()) {
        toast.error('URL is required');
        return;
      }

      // If adding new, check if platform already exists
      if (!editingProfile) {
        const exists = profiles.some(p => p.platform === profileForm.platform);
        if (exists) {
          toast.error(`A profile for ${profileForm.platform} already exists. Please edit it instead.`);
          return;
        }
      }

      if (editingProfile) {
        await axios.put(
          `${API_URL}/api/social_feeds/${editingProfile.id}`,
          profileForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Profile updated successfully!');
      } else {
        await axios.post(
          `${API_URL}/api/social_feeds`,
          profileForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Profile added successfully!');
      }

      setProfileDialogOpen(false);
      setEditingProfile(null);
      resetProfileForm();
      await fetchData();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    }
  };

  const handleEditProfile = (profile) => {
    setEditingProfile(profile);
    setProfileForm({
      platform: profile.platform,
      url: profile.url,
      is_active: profile.is_active
    });
    setProfileDialogOpen(true);
  };

  const handleDeleteProfile = async (id) => {
    if (!window.confirm('Are you sure you want to delete this profile link?')) return;

    try {
      await axios.delete(
        `${API_URL}/api/social_feeds/${id}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Profile deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Error deleting profile:', error);
      toast.error('Failed to delete profile');
    }
  };

  const resetProfileForm = () => {
    setProfileForm({
      platform: 'facebook',
      url: '',
      is_active: true
    });
  };

  const getPlatformColor = (platform) => {
    const colors = {
      facebook: 'bg-[#1877F2]',
      twitter: 'bg-[#1DA1F2]',
      instagram: 'bg-gradient-to-r from-purple-500 to-pink-500',
      youtube: 'bg-[#FF0000]'
    };
    return colors[platform] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-[#0E6D2B]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Globe className="w-7 h-7 text-[#0E6D2B]" />
            Social Media Management
          </h1>
          <p className="text-gray-600 text-sm mt-1">Manage social media profiles and posts</p>
        </div>

        <Tabs defaultValue="posts" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="posts">Social Media Posts</TabsTrigger>
            <TabsTrigger value="profiles">Profile Links</TabsTrigger>
          </TabsList>

          {/* Posts Management Tab */}
          <TabsContent value="posts" className="space-y-4">
            <div className="bg-white rounded-lg border p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-lg font-semibold">Manage Social Media Posts</h2>
                  <p className="text-sm text-gray-500">Add recent posts from your social media platforms</p>
                </div>
                <Button
                  onClick={() => {
                    setEditingPost(null);
                    resetPostForm();
                    setDialogOpen(true);
                  }}
                  className="bg-[#0E6D2B] hover:bg-[#0a5a23]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Post
                </Button>
              </div>

              {/* Posts List */}
              <div className="space-y-3">
                {posts.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No posts added yet. Click "Add Post" to create your first social media post.</p>
                  </div>
                ) : (
                  posts.map(post => (
                    <div key={post.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`${getPlatformColor(post.platform)} text-white text-xs px-2 py-1 rounded-full font-semibold`}>
                              {post.platform.toUpperCase()}
                            </span>
                            <span className="text-xs text-gray-500">{post.post_date}</span>
                          </div>
                          <p className="text-sm text-gray-700 mb-2 line-clamp-3">{post.content}</p>
                          {post.post_url && (
                            <a
                              href={post.post_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                            >
                              View Original Post <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditPost(post)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeletePost(post.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {post.image_url && (
                        <img
                          src={post.image_url}
                          alt="Post"
                          className="mt-3 w-full max-w-md h-48 object-cover rounded-lg"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Profile Links Tab */}
          <TabsContent value="profiles" className="space-y-4">
            <div className="bg-white rounded-lg border p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-lg font-semibold">Social Media Profile Links</h2>
                  <p className="text-sm text-gray-600">
                    Manage social media profile URLs that appear in the footer
                  </p>
                </div>
                <Button
                  onClick={() => {
                    setEditingProfile(null);
                    resetProfileForm();
                    setProfileDialogOpen(true);
                  }}
                  className="bg-[#0E6D2B] hover:bg-[#0a5a23]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Profile Link
                </Button>
              </div>

              <div className="space-y-3">
                {profiles.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Globe className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No social media profiles configured yet.</p>
                  </div>
                ) : (
                  profiles.map(profile => (
                    <div key={profile.id} className="flex items-center justify-between p-4 border rounded-lg hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-3 flex-1">
                        <span className={`${getPlatformColor(profile.platform)} text-white text-xs px-3 py-1 rounded-full font-semibold`}>
                          {profile.platform.toUpperCase()}
                        </span>
                        <a
                          href={profile.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline truncate max-w-md"
                        >
                          {profile.url}
                        </a>
                        <span className={`text-xs px-2 py-0.5 rounded ${profile.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {profile.is_active ? '✓ Active' : '✗ Inactive'}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditProfile(profile)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteProfile(profile.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Add/Edit Post Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingPost ? 'Edit' : 'Add'} Social Media Post</DialogTitle>
            </DialogHeader>

            <form onSubmit={(e) => { e.preventDefault(); handleSavePost(); }} className="space-y-4">
              <div>
                <Label>Platform *</Label>
                <Select
                  value={postForm.platform}
                  onValueChange={(val) => setPostForm({ ...postForm, platform: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map(platform => (
                      <SelectItem key={platform} value={platform}>
                        {platform.charAt(0).toUpperCase() + platform.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Post Content *</Label>
                <Textarea
                  rows={4}
                  required
                  value={postForm.content}
                  onChange={(e) => setPostForm({ ...postForm, content: e.target.value })}
                  placeholder="Enter the post content/caption..."
                />
                <p className="text-xs text-gray-500 mt-1">Copy the actual post text from your social media</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Post Date *</Label>
                  <Input
                    type="date"
                    required
                    value={postForm.post_date}
                    onChange={(e) => setPostForm({ ...postForm, post_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Post URL (Optional)</Label>
                  <Input
                    type="url"
                    value={postForm.post_url}
                    onChange={(e) => setPostForm({ ...postForm, post_url: e.target.value })}
                    placeholder="https://facebook.com/icarcish/posts/..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Link to the original post</p>
                </div>
              </div>

              <div>
                <Label>Image (Optional)</Label>
                <FileUploadInput
                  field={{ name: 'image_url', type: 'file', fileType: 'image' }}
                  value={fileData.post_image}
                  existingUrl={postForm.image_url}
                  onChange={(files) => setFileData({ ...fileData, post_image: files })}
                  onDeleteServerFile={() => { setPostForm({ ...postForm, image_url: '' }); return true; }}
                />
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button type="submit">
                  {editingPost ? 'Update Post' : 'Add Post'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    setEditingPost(null);
                    setFileData({});
                    resetPostForm();
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add/Edit Profile Dialog */}
        <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingProfile ? 'Edit' : 'Add'} Profile Link</DialogTitle>
            </DialogHeader>

            <form onSubmit={(e) => { e.preventDefault(); handleSaveProfile(); }} className="space-y-4">
              <div>
                <Label>Platform *</Label>
                <Select
                  value={profileForm.platform}
                  onValueChange={(val) => setProfileForm({ ...profileForm, platform: val })}
                  disabled={!!editingProfile} // Disable platform change when editing
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map(platform => (
                      <SelectItem key={platform} value={platform}>
                        {platform.charAt(0).toUpperCase() + platform.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editingProfile && <p className="text-xs text-gray-500 mt-1">Platform cannot be changed once created.</p>}
              </div>

              <div>
                <Label>Profile URL *</Label>
                <Input
                  type="url"
                  required
                  value={profileForm.url}
                  onChange={(e) => setProfileForm({ ...profileForm, url: e.target.value })}
                  placeholder={`https://${profileForm.platform}.com/...`}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={profileForm.is_active}
                  onChange={(e) => setProfileForm({ ...profileForm, is_active: e.target.checked })}
                  className="rounded border-gray-300 text-[#0E6D2B] focus:ring-[#0E6D2B]"
                />
                <Label htmlFor="isActive" className="mb-0 cursor-pointer">Active</Label>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button type="submit">
                  {editingProfile ? 'Update Profile' : 'Add Profile'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setProfileDialogOpen(false);
                    setEditingProfile(null);
                    resetProfileForm();
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
